import React, { useState, useEffect } from 'react';
import CodeEditor from './components/CodeEditor';
import PreviewPane from './components/PreviewPane';
import ExampleTemplates from './components/ExampleTemplates';
import FilesExplorer from './components/FilesExplorer';
import { Play } from 'lucide-react';
import { CodeEditorProps } from './types';

const defaultCode = `function Component() {
  return (
    <div className="p-8 text-center">
      <h1 className="text-3xl font-bold text-blue-600 mb-4">
        Hello, React!
      </h1>
      <p className="text-gray-600">
        Welcome to the React Code Editor
      </p>
    </div>
  );
}`;

function App(props: CodeEditorProps) {
  const {
    initialCode = defaultCode,
    codeType = 'react',
    className = '',
    theme = 'light',
    showFilesExplorer = true,
    showPreview = true,
    showTemplates = true,
    height = '100vh',
    width = '100%'
  } = props;

  const [code, setCode] = useState(initialCode);
  const [isPreviewVisible, setIsPreviewVisible] = useState(showPreview);
  const [executedCode, setExecutedCode] = useState(initialCode);
  const [isMobile, setIsMobile] = useState(false);
  const [currentCodeType, setCurrentCodeType] = useState<'react' | 'nodejs'>(codeType);
  const [filesKey, setFilesKey] = useState(0); // Key to force FilesExplorer refresh

  // Apply theme
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleRunCode = () => {
    setExecutedCode(code);
  };

  const handleSelectTemplate = (templateCode: string, type: 'react' | 'nodejs') => {
    setCode(templateCode);
    setExecutedCode(templateCode);
    setCurrentCodeType(type);
    // Force FilesExplorer to refresh when template changes
    setFilesKey(prev => prev + 1);
  };

  const handleFileSelect = (filePath: string, content: string) => {
    // Update the code editor with the selected file content
    setCode(content);
    setExecutedCode(content);
    
    // Determine code type based on file extension
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (extension === 'jsx' || extension === 'tsx') {
      setCurrentCodeType('react');
    } else if (extension === 'js' && filePath.includes('server')) {
      setCurrentCodeType('nodejs');
    }
  };

  const containerStyle = {
    height,
    width,
    ...(className && { className })
  };

  if (isMobile) {
    return (
      <div style={containerStyle} className="h-screen flex flex-col bg-gray-100">
        {showTemplates && (
          <ExampleTemplates onSelectTemplate={handleSelectTemplate} />
        )}

        <div className="flex-1 overflow-hidden">
          {isPreviewVisible ? (
            <PreviewPane
              code={executedCode}
              isVisible={true}
              onToggleVisibility={() => {}}
            />
          ) : (
            <CodeEditor
              code={code}
              codeType={currentCodeType}
              onChange={setCode}
              onRun={handleRunCode}
            />
          )}
        </div>

        {!isPreviewVisible && (
          <div className="p-4 bg-white border-t border-gray-200">
            <button
              onClick={handleRunCode}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Play size={16} />
              <span>Run Code</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle} className="h-screen flex bg-gray-100">
      {/* Files Explorer - Only show if enabled */}
      {showFilesExplorer && (
        <div className="w-80 border-r border-gray-200 bg-white overflow-y-auto">
          <FilesExplorer 
            key={filesKey}
            onFileSelect={handleFileSelect} 
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {showTemplates && (
          <ExampleTemplates onSelectTemplate={handleSelectTemplate} />
        )}

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1">
            <CodeEditor
              code={code}
              codeType={currentCodeType}
              onChange={setCode}
              onRun={handleRunCode}
            />
          </div>
          {showPreview && (
            <div className="flex-1">
              <PreviewPane
                code={executedCode}
                isVisible={isPreviewVisible}
                onToggleVisibility={() => setIsPreviewVisible(!isPreviewVisible)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;